// LoadingScreen.tsx

import {
    createContext,
    useContext,
    useState,
    type ReactNode,
    type FC,
} from "react";
import "./LoadingScreen.scss";

/* ──────────────────────────────── */
/*  1 ▸ Define every variant here   */
/* ──────────────────────────────── */

import TaskEnvLoadingScreen from "./TaskEnvLoadingScreen/TaskEnvLoadingScreen";

export type LoaderVariant = "task-env";

/* Map a variant string to its visual component */
const loaderMap: Record<LoaderVariant, FC<{ onComplete?: () => void }>> = {
    "task-env": TaskEnvLoadingScreen,
};

/* ──────────────────────────────── */
/*  2 ▸ Context, provider & hook    */
/* ──────────────────────────────── */

interface LoadingCtx {
    showLoading: (variant?: LoaderVariant, onComplete?: () => void) => void;
    hideLoading: () => void;
}

interface LoadingState {
    visible: boolean;
    variant: LoaderVariant;
    onComplete?: () => void;
}

const LoadingContext = createContext<LoadingCtx | null>(null);

export function LoadingProvider({ children }: { children: ReactNode }) {
    const [state, setState] = useState<LoadingState>({
        visible: false,
        variant: "task-env",
    });

    const showLoading = (
        variant: LoaderVariant = "task-env",
        onComplete?: () => void,
    ) => setState({ visible: true, variant, onComplete });

    const hideLoading = () => setState((s) => ({ ...s, visible: false }));

    const Overlay = loaderMap[state.variant] as FC<{
        onComplete?: () => void;
    }>;

    return (
        <LoadingContext.Provider value={{ showLoading, hideLoading }}>
            {children}
            {state.visible && <Overlay onComplete={state.onComplete} />}
        </LoadingContext.Provider>
    );
}

export function useLoading(): LoadingCtx {
    const ctx = useContext(LoadingContext);
    if (!ctx) throw new Error("useLoading must be inside LoadingProvider");
    return ctx;
}
